import React from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { AutoFetchButton } from '@core/Components/AutoFetchButton'
import { getBetLoopSearchProfiles } from '@core/Areas/BetLoop/helpers/api/index'
import { BetLoopFeedPageSize } from '@core/Areas/BetLoop/constants'

export const useSearchAllProfiles = (searchQuery: string | undefined, pageSize = 25) => {
  const query = searchQuery?.trim()

  const { data, isLoading, isRefetching, refetch, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        ...queryKeys.betLoopAccountExploreProfiles,
        { pageSize, searchQuery: query || '' },
      ],
      queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
        if (query === undefined) return undefined
        return getBetLoopSearchProfiles({
          pageSize: pageSize ?? BetLoopFeedPageSize,
          searchQuery: query,
          pageParam,
        })
      },

      initialPageParam: undefined,

      getNextPageParam: lastPage => {
        if (!lastPage || lastPage.length < Number(pageSize ?? BetLoopFeedPageSize)) return undefined
        return lastPage[lastPage.length - 1]?.id ?? undefined
      },

      staleTime: 1_000 * 60 * 2, // 2min
      gcTime: 1_000 * 60 * 5,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    })

  const isEmptyState = data?.pages[0]?.length === 0

  const isFinalPage =
    (data?.pages?.[data.pages.length - 1]?.length ?? 0) < (pageSize ?? BetLoopFeedPageSize)

  return {
    data,
    isLoading,
    refetch,
    isRefetching,
    isEmptyState,
    LoadMoreButton: () =>
      isFinalPage || isEmptyState || isLoading ? null : (
        <AutoFetchButton onClick={fetchNextPage} isFetching={isFetchingNextPage} />
      ),
  }
}
